import * as yup from 'yup';
import { ExternalOrderRequest } from '@api/ringil-api.v2.tsx';
import { NO_ORDERS_NOW_ORDER_NUMBER } from './common.tsx';

// `externalIdPresent` is opt-in (default false). When true (PAP), orderName becomes optional and `externalOrders`
// is required — mirrors common.tsx `orderSchema`. Callers that omit it are byte-identical to before.
export const createOrdersComponentValidationSchema = (config: OrdersConfig, wrap: boolean, tr: any, externalIdPresent: boolean = false) => {
  const visibilitySchema = (visibility: 'always' | 'optional' | 'never', required: boolean, schema: any) => {
    if (visibility === 'never') {
      return yup.mixed().nullable(true);
    }
    if (required) {
      return schema;
    }
    return schema.nullable().notRequired();
  };

  const visibilitySchemaWithNotDef = (visibility: 'always' | 'optional' | 'never', required: boolean, schema: any, notRequiredSchema: any) => {
    if (visibility === 'never') {
      return yup.mixed().nullable(true);
    }
    return required ? schema : notRequiredSchema;
  };

  const orderSchemaNoWrapped = yup.object().shape({
    orderName: externalIdPresent
      ? yup.string().nullable()
      : visibilitySchema(
          config.orderName.visibility,
          config.orderName.required,
          yup
            .string()
            .required('Order name is required')
            .min(1, 'Order name must be at least 1 character')
            .test('not-empty', 'Order name cannot be empty', value => value && value.trim().length > 0),
        ),
    externalOrders: externalIdPresent
      ? yup
          .array()
          .of(yup.object().shape({ orderNumber: yup.string().required() }))
          .min(1, 'externalOrdersRequired')
          .required('externalOrdersRequired')
          .test('externalOrdersNoOrdersNowExclusive', 'externalOrdersNoOrdersNowExclusive', value => {
            if (!value || value.length < 2) return true;
            const list = value as ExternalOrderRequest[];
            return !list.some(v => v.orderNumber === NO_ORDERS_NOW_ORDER_NUMBER);
          })
          .test('externalOrdersIntersect', 'externalOrdersNoIntersection', value => {
            if (!value || value.length < 2) return true;
            const toMs = (s?: string) => {
              if (!s) return null;
              const t = new Date(s).getTime();
              return Number.isNaN(t) ? null : t;
            };
            const list = value as ExternalOrderRequest[];
            const starts = list.map(v => toMs(v.timeFrom)).filter((n): n is number => n !== null);
            const ends = list.map(v => toMs(v.timeTo)).filter((n): n is number => n !== null);
            if (starts.length === 0 || ends.length === 0) return true;
            return Math.max(...starts) <= Math.min(...ends);
          })
      : yup.array().nullable(),
    items: yup
      .array()
      .min(config.cargo.visibility === 'always' ? 1 : 0, tr('CreateOrdersComponentValidationSchema.oneItemMinimum', 'Zadejte minimálně jednu položku'))
      .of(
        yup.object().shape({
          amount: visibilitySchema(config.cargo.visibility, config.cargo.required, yup.string()),
          cargoTemplateCode: visibilitySchema(config.cargo.visibility, config.cargo.required, yup.string()),
          width: yup.number().when('cargoTemplateCode', {
            is: (code: string) => code !== 'LOADING_METER',
            then: schema => visibilitySchema(
              config.cargoDimensions.visibility,
              config.cargo.visibility !== 'never' && config.cargoDimensions.items.width.required,
              schema.required('Width is required').moreThan(0, 'Width must be greater than 0').typeError('Width must be a number')
            ),
            otherwise: schema => schema.nullable().notRequired()
          }),
          height: yup.number().when('cargoTemplateCode', {
            is: (code: string) => code !== 'LOADING_METER',
            then: schema => visibilitySchema(
              config.cargoDimensions.visibility,
              config.cargo.visibility !== 'never' && config.cargoDimensions.items.height.required,
              schema.required('Height is required').moreThan(0, 'Height must be greater than 0').typeError('Height must be a number')
            ),
            otherwise: schema => schema.nullable().notRequired()
          }),
          length: yup.number().when('cargoTemplateCode', {
            is: (code: string) => code !== 'LOADING_METER',
            then: schema => visibilitySchema(
              config.cargoDimensions.visibility,
              config.cargo.visibility !== 'never' && config.cargoDimensions.items.length.required,
              schema.required('Length is required').moreThan(0, 'Length must be greater than 0').typeError('Length must be a number')
            ),
            otherwise: schema => schema.nullable().notRequired()
          }),
          weight: visibilitySchema(
            config.cargoDimensions.visibility,
            config.cargo.visibility !== 'never' && config.cargoDimensions.items.weight.required,
            yup.number().required('Weight is required').moreThan(0, 'Weight must be greater than 0').typeError('Weight must be a number'),
          ),
        }),
      ),

    orderTotalWeight: yup.number().when('items', {
        is: (items: any[]) => !items || items.length === 0,
        then: () => config.orderTotalWeight.required
          ? yup.number().required('Total weight is required')
          : yup.number().nullable(),
        otherwise: () => yup.number().nullable()
      }),

    orderAmount: visibilitySchema(
      config.cargoDetails.visibility,
      config.cargoDetails.items.orderAmount.required,
      yup.number().typeError('Order amount must be a number').required('Order amount is required').moreThan(0, 'Order amount must be greater than 0'),
    ),
    orderDescription: visibilitySchema(config.cargoDetails.visibility, config.cargoDetails.items.orderDescription.required, yup.string()),
    orderValue: visibilitySchemaWithNotDef(
      config.cargoDetails.visibility,
      config.cargoDetails.items.orderValue.required,
      // This branch only runs when config.cargoDetails.items.orderValue.required is true. The requirement is attached at the
      // object level (test named 'required') so the error path is `orders[i].orderValue` — exactly what the OrdersComponent
      // Field looks up. A nested price.required() would emit at `orders[i].orderValue.price`, which the exact-match
      // findValidationMessage never finds. (The old `$isCargoDetailsOpen` gate also never fired — that context var is local
      // UI state, not passed to form-level validation.)
      yup
        .object()
        .shape({ price: yup.string().nullable(), currency: yup.string().nullable() })
        .test('required', (value: any) => !!(value && value.price?.trim().length > 0 && value.currency?.trim().length > 0)),
      yup.mixed().nullable().optional(),
    ),
    unloadingLocationId: visibilitySchema(
      config.unloadingLocationId.visibility,
      config.unloadingLocationId.required,
      yup.string().required('Hodnota musí být zadána')
    ),
  });

  if (wrap) {
    return yup.object().shape({
      orders: yup.array().of(orderSchemaNoWrapped),
    });
  } else {
    return orderSchemaNoWrapped;
  }
};

export const expeditionConfig = (isItemDimensionsRequired: boolean = true): OrdersConfig => ({
  orderName: { visibility: 'always', required: true },
  cargo: { visibility: 'optional', required: true },
  cargoDimensions: {
    visibility: 'always',
    required: true,
    items: {
      width: { required: isItemDimensionsRequired },
      height: { required: isItemDimensionsRequired },
      length: { required: isItemDimensionsRequired },
      weight: { required: isItemDimensionsRequired },
    },
  },
  cargoDetails: {
    visibility: 'optional',
    required: false,
    items: { orderAmount: { required: false }, orderDescription: { required: false }, orderValue: { required: false } },
  },
  orderTotalWeight: {
    render: true,
    required: true,
  },
  unloadingLocationId: {
    render: false,
    required: false,
    visibility: 'never',
  },
});

export const fullRequiredConfig= (isItemDimensionsRequired: boolean = true): OrdersConfig => ({
  orderName: { visibility: 'always', required: true },
  cargo: { visibility: 'optional', required: true },
  cargoDimensions: {
    visibility: 'always',
    required: true,
    items: {
      width: { required: isItemDimensionsRequired },
      height: { required: isItemDimensionsRequired },
      length: { required: isItemDimensionsRequired },
      weight: { required: isItemDimensionsRequired },
    },
  },
  cargoDetails: {
    visibility: 'optional',
    required: false,
    items: { orderAmount: { required: false }, orderDescription: { required: false }, orderValue: { required: false } },
  },
  orderTotalWeight: {
    render: true,
  },
  unloadingLocationId: {
    render: true,
    required: true,
    visibility: 'always',
  },
});

export interface OrdersConfig {
  orderName: { visibility: Visibility; required: boolean };
  // jedna se o manipulacni jednotku a pocet
  cargo: { visibility: Visibility; required: boolean };
  // jedna se o sirku, vysku, delku a vahu
  cargoDimensions: {
    visibility: Visibility;
    required: boolean;
    items: {
      width: { required: boolean };
      height: { required: boolean };
      length: { required: boolean };
      weight: { required: boolean };
    };
  };
  cargoDetails: {
    visibility: Visibility;
    required: boolean;
    items: {
      orderAmount: { required: boolean };
      orderDescription: { required: boolean };
      orderValue: { required: boolean };
    };
  };
  orderTotalWeight: {
    render: boolean;
    required?: boolean;
  };
  unloadingLocationId: {
    render: boolean;
    required?: boolean;
    visibility?: Visibility;
  };
  // Additive per-item note knob (SN drives it from manipulationUnitNote). Absent ⇒ shown (reservation/expedition unchanged).
  itemNote?: { visibility: Visibility };
  // Additive per-item stackable knob (SN drives it from the stackable setting). Absent ⇒ shown (reservation/expedition unchanged).
  stackable?: { visibility: Visibility };
}

export type Visibility = 'always' | 'never' | 'optional';
